import React, { useState, useRef } from 'react';
import {
  Container, Card, Typography, TextField, FormControlLabel, Checkbox,
  Button, Box, Stack, Alert, AlertColor, AlertTitle,
  Divider, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  CircularProgress
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import SignaturePad, { SignaturePadRef } from '../Components/SignaturePad.tsx';
import PublicApiService from '../Services/publicApiService.ts';

const Under18ConsentForm = () => {
  const initialFormState = {
    // Minor's Information
    minorFirstName: '',
    minorLastName: '',
    minorDateOfBirth: '',
    minorAge: '',
    minorGender: '',
    // Parent/Guardian Information
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    relationship: '',
    address: '',
    // Medical Info
    physician: '',
    allergies: '',
    bleedingDisorders: '',
    medicalConditions: '',
    medications: '',
    // Emergency & Referral
    emergencyContact: '',
    howDidYouHear: '',
    // Procedure
    typeOfPiercing: '',
    typeOfJewelryUsed: '',
    nameOfPiercer: '',
    // Acknowledgments
    acknowledgments: {} as { [key: number]: { checked: boolean; label: string } },
    // Dates
    date: '',
    piercerDate: '',
    // Photo ID
    photoIdVerified: '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [phoneError, setPhoneError] = useState('');
  const guardianSigRef = useRef<SignaturePadRef>(null);
  const piercerSigRef = useRef<SignaturePadRef>(null);
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: '',
    severity: 'warning',
  });

  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Guardian ID upload state
  const [guardianIdFile, setGuardianIdFile] = useState<File | null>(null);
  const [guardianIdPreview, setGuardianIdPreview] = useState<string | null>(null);
  const [guardianIdFileName, setGuardianIdFileName] = useState<string>('');
  const guardianFileInputRef = useRef<HTMLInputElement>(null);
  const guardianCameraInputRef = useRef<HTMLInputElement>(null);

  // Notarized document upload state
  const [notarizedFile, setNotarizedFile] = useState<File | null>(null);
  const [notarizedPreview, setNotarizedPreview] = useState<string | null>(null);
  const [notarizedFileName, setNotarizedFileName] = useState<string>('');
  const notarizedFileInputRef = useRef<HTMLInputElement>(null);
  const notarizedCameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFile: (val: File | null) => void,
    setPreview: (val: string | null) => void,
    setFileName: (val: string) => void
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setAlert({ open: true, message: 'Please upload an image file (JPG, PNG, etc.).', severity: 'error' });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setAlert({ open: true, message: 'Photo must be less than 10MB.', severity: 'error' });
      return;
    }

    setFile(file);
    setFileName(file.name);

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeGuardianId = () => {
    setGuardianIdFile(null);
    setGuardianIdPreview(null);
    setGuardianIdFileName('');
    if (guardianFileInputRef.current) guardianFileInputRef.current.value = '';
    if (guardianCameraInputRef.current) guardianCameraInputRef.current.value = '';
  };

  const removeNotarizedDoc = () => {
    setNotarizedFile(null);
    setNotarizedPreview(null);
    setNotarizedFileName('');
    if (notarizedFileInputRef.current) notarizedFileInputRef.current.value = '';
    if (notarizedCameraInputRef.current) notarizedCameraInputRef.current.value = '';
  };

  // Auto-calculate age from DOB
  const calculateAge = (dob: string): number | null => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    if (birthDate >= today) return null; // Future date
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const [dobError, setDobError] = useState('');
  const [ageError, setAgeError] = useState('');

  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dob = e.target.value;
    setFormData((prev) => ({ ...prev, minorDateOfBirth: dob }));

    if (!dob) {
      setDobError('');
      setAgeError('');
      return;
    }

    const birthDate = new Date(dob);
    const today = new Date();

    if (birthDate >= today) {
      setDobError('Date of birth must be in the past.');
      setFormData((prev) => ({ ...prev, minorAge: '' }));
      return;
    }

    setDobError('');
    const age = calculateAge(dob);
    if (age !== null) {
      if (age >= 18) {
        setAgeError('Minor must be under 18 years old.');
      } else if (age < 0) {
        setAgeError('Invalid age.');
      } else {
        setAgeError('');
      }
      setFormData((prev) => ({ ...prev, minorAge: String(age) }));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let phoneNumber = e.target.value.replace(/\D/g, '');

    if (phoneNumber.startsWith('1')) {
      phoneNumber = phoneNumber.slice(1);
    }

    if (phoneNumber.length > 10) {
      phoneNumber = phoneNumber.slice(0, 10);
    }

    if (phoneNumber.length !== 10 && phoneNumber.length > 0) {
      setPhoneError('Phone number must be exactly 10 digits.');
    } else {
      setPhoneError('');
    }

    setFormData({ ...formData, phone: phoneNumber });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name !== 'phone') {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleAcknowledgment = (index: number, label: string) => {
    setFormData({
      ...formData,
      acknowledgments: {
        ...formData.acknowledgments,
        [index]: { checked: !formData.acknowledgments[index]?.checked, label }
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (phoneError) {
      setAlert({ open: true, message: 'Please enter a valid 10-digit phone number.', severity: 'error' });
      return;
    }

    if (formData.phone.length !== 10) {
      setAlert({ open: true, message: 'Please enter a valid 10-digit phone number.', severity: 'error' });
      return;
    }

    // Validate DOB
    if (dobError) {
      setAlert({ open: true, message: dobError, severity: 'error' });
      return;
    }

    // Validate age
    if (ageError) {
      setAlert({ open: true, message: ageError, severity: 'error' });
      return;
    }

    const age = calculateAge(formData.minorDateOfBirth);
    if (age === null || age >= 18 || age < 0) {
      setAlert({ open: true, message: 'Minor must be under 18 years old. Please check the date of birth.', severity: 'error' });
      return;
    }

    // Validate all acknowledgments are checked
    const allChecked = acknowledgmentStatements.every(
      (_, index) => formData.acknowledgments[index]?.checked
    );
    if (!allChecked) {
      setAlert({ open: true, message: 'Please acknowledge all consent statements.', severity: 'error' });
      return;
    }

    // Validate guardian signature
    if (guardianSigRef.current?.isEmpty()) {
      setAlert({ open: true, message: 'Please provide the parent/guardian signature.', severity: 'error' });
      return;
    }

    // Validate piercer signature
    if (piercerSigRef.current?.isEmpty()) {
      setAlert({ open: true, message: 'Piercer signature is required.', severity: 'error' });
      return;
    }

    // Validate piercer date
    if (!formData.piercerDate) {
      setAlert({ open: true, message: 'Piercer date is required.', severity: 'error' });
      return;
    }

    // Validate guardian ID upload
    if (!guardianIdFile) {
      setAlert({ open: true, message: 'Please upload a photo of the guardian\'s ID.', severity: 'error' });
      return;
    }

    // Validate notarized document upload
    if (!notarizedFile) {
      setAlert({ open: true, message: 'Please upload a photo of the notarized consent document.', severity: 'error' });
      return;
    }

    try {
      setSubmitting(true);
      const apiService = new PublicApiService();

      // Step 1: Get presigned upload URLs for photos
      const filesToUpload = [];
      if (guardianIdFile) {
        filesToUpload.push({ fileName: guardianIdFile.name, contentType: guardianIdFile.type || 'image/jpeg' });
      }
      if (notarizedFile) {
        filesToUpload.push({ fileName: notarizedFile.name, contentType: notarizedFile.type || 'image/jpeg' });
      }

      let guardianIdS3Key = '';
      let notarizedDocS3Key = '';

      if (filesToUpload.length > 0) {
        const uploadUrlResponse = await apiService.post('/upload-url', { files: filesToUpload });
        const uploadUrls = uploadUrlResponse.uploadUrls;

        // Step 2: Upload files directly to S3 using presigned URLs
        let urlIndex = 0;
        if (guardianIdFile) {
          const { uploadUrl, s3Key } = uploadUrls[urlIndex];
          await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': guardianIdFile.type || 'image/jpeg',
              'x-amz-server-side-encryption': 'AES256',
            },
            body: guardianIdFile,
          });
          guardianIdS3Key = s3Key;
          urlIndex++;
        }
        if (notarizedFile) {
          const { uploadUrl, s3Key } = uploadUrls[urlIndex];
          await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': notarizedFile.type || 'image/jpeg',
              'x-amz-server-side-encryption': 'AES256',
            },
            body: notarizedFile,
          });
          notarizedDocS3Key = s3Key;
        }
      }

      // Step 3: Submit the form data with S3 keys (no base64 photos)
      const submitData = {
        ...formData,
        formType: 'minor',
        signature: guardianSigRef.current?.toDataURL() || '',
        piercerSignature: piercerSigRef.current?.toDataURL() || '',
        photoIdS3Key: guardianIdS3Key,
        notarizedDocS3Key: notarizedDocS3Key,
        photoIdVerified: 'yes',
      };
      await apiService.postConsentData(submitData);

      setFormData(initialFormState);
      guardianSigRef.current?.clear();
      piercerSigRef.current?.clear();
      removeGuardianId();
      removeNotarizedDoc();
      setDobError('');
      setAgeError('');
      setSuccessModalOpen(true);
    } catch (error) {
      setAlert({ open: true, message: 'Error submitting the form. Please try again.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const acknowledgmentStatements = [
    'I, the parent/legal guardian, consent to my minor child receiving a body piercing procedure.',
    'I acknowledge that the risks of infection, swelling, and rejection have been explained to me.',
    'I understand that I must be present with valid ID during the procedure.',
    'I confirm that I have provided proper identification and proof of guardianship.',
    'I certify that neither myself nor the minor are under the influence of alcohol or drugs.',
    'I accept full responsibility for any consequences that may result from my decision to allow my minor child to receive a body piercing.',
    'I acknowledge that my minor child will receive a copy of the Body Piercing Aftercare Instructions along with verbal instructions. I agree to ensure my child reads and follows all aftercare instructions.',
    'I understand that all procedures are performed under strict hygienic conditions.',
    'I confirm the minor has no medical conditions that would contraindicate this procedure.',
    'I certify that the minor has eaten within the last 4-6 hours before the appointment.',
    'I acknowledge and agree that all piercing services and session fees are non-refundable. I understand that Jasel is not responsible for lost, broken, damaged, or loosened jewelry.',
    'I consent to receiving communication, including appointment reminders and follow-ups, via text message and phone calls at the number provided.',
    'I agree not to bring any legal action or claim against Jasel Jewelry, its owners, employees, contractors, agents, affiliated companies, or associated personnel for issues arising from or related to my voluntary decision to allow my minor child to receive body piercing services at this establishment, except to the extent such waiver or limitation is prohibited by applicable law.',
  ];

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      {alert.open && (
        <Stack sx={{ width: '100%', mb: 2 }} spacing={2}>
          <Alert
            severity={alert.severity}
            onClose={() => setAlert({ ...alert, open: false })}
          >
            <AlertTitle>{alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}</AlertTitle>
            {alert.message}
          </Alert>
        </Stack>
      )}

      <Card sx={{ p: 4, boxShadow: 3 }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          MINOR PIERCING CONSENT FORM
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          {/* Minor's Information */}
          <Typography variant="h5" sx={{ mb: 2, color: '#555', fontWeight: 'bold' }}>
            Minor's Information
          </Typography>

          <Stack spacing={2} direction="row">
            <TextField
              fullWidth label="Minor's First Name" name="minorFirstName"
              value={formData.minorFirstName} onChange={handleChange} required
            />
            <TextField
              fullWidth label="Minor's Last Name" name="minorLastName"
              value={formData.minorLastName} onChange={handleChange} required
            />
          </Stack>

          <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
            <TextField
              fullWidth label="Date of Birth" type="date" name="minorDateOfBirth"
              value={formData.minorDateOfBirth} onChange={handleDobChange} required
              InputLabelProps={{ shrink: true }}
              error={Boolean(dobError)} helperText={dobError}
              inputProps={{ max: new Date().toISOString().split('T')[0] }}
            />
            <TextField
              label="Age" name="minorAge" value={formData.minorAge}
              required sx={{ minWidth: 100 }}
              InputProps={{ readOnly: true }}
              error={Boolean(ageError)} helperText={ageError || 'Auto-calculated'}
            />
            <TextField
              fullWidth label="Gender" name="minorGender"
              value={formData.minorGender} onChange={handleChange}
            />
          </Stack>

          {/* Parent/Guardian Information */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, color: '#555', fontWeight: 'bold' }}>
            Parent/Guardian Information
          </Typography>

          <Stack spacing={2} direction="row">
            <TextField
              fullWidth label="Guardian's First Name" name="firstName"
              value={formData.firstName} onChange={handleChange} required
            />
            <TextField
              fullWidth label="Guardian's Last Name" name="lastName"
              value={formData.lastName} onChange={handleChange} required
            />
          </Stack>

          <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
            <TextField
              fullWidth label="Phone" type="tel" name="phone"
              value={formData.phone ? `+1 ${formData.phone}` : ''}
              onChange={handlePhoneChange} required
              error={Boolean(phoneError)} helperText={phoneError}
              placeholder="+1 (xxx) xxx-xxxx"
            />
            <TextField
              fullWidth label="Email" type="email" name="email"
              value={formData.email} onChange={handleChange} required
            />
          </Stack>

          <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
            <TextField
              fullWidth label="Relationship to Minor" name="relationship"
              value={formData.relationship} onChange={handleChange} required
            />
          </Stack>

          <TextField
            fullWidth label="Address" name="address"
            value={formData.address} onChange={handleChange} required
            sx={{ mt: 2 }}
          />

          <TextField
            fullWidth label="Physician" name="physician"
            value={formData.physician} onChange={handleChange}
            sx={{ mt: 2 }}
          />

          {/* Medical History */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            Does the minor have any:
          </Typography>

          <Stack spacing={2}>
            <TextField
              fullWidth label="Allergies" name="allergies"
              value={formData.allergies} onChange={handleChange}
            />
            <TextField
              fullWidth label="Bleeding Disorders" name="bleedingDisorders"
              value={formData.bleedingDisorders} onChange={handleChange}
            />
            <TextField
              fullWidth label="Medical Conditions" name="medicalConditions"
              value={formData.medicalConditions} onChange={handleChange}
            />
            <TextField
              fullWidth label="Medications" name="medications"
              value={formData.medications} onChange={handleChange}
            />
          </Stack>

          {/* Emergency & Referral */}
          <Divider sx={{ my: 3 }} />
          <TextField
            fullWidth label="Emergency Contact (Name & Phone)" name="emergencyContact"
            value={formData.emergencyContact} onChange={handleChange} required
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth label="How did you hear about us?" name="howDidYouHear"
            value={formData.howDidYouHear} onChange={handleChange}
            sx={{ mt: 2 }}
          />

          {/* Consent & Acknowledgment */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, color: '#555', fontWeight: 'bold' }}>
            Consent & Acknowledgment
          </Typography>

          <Stack spacing={1.5}>
            {acknowledgmentStatements.map((statement, index) => (
              <Card key={index} sx={{ display: 'flex', alignItems: 'flex-start', p: 2, boxShadow: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.acknowledgments[index]?.checked || false}
                      onChange={() => handleAcknowledgment(index, statement)}
                      sx={{ mr: 1 }}
                    />
                  }
                  label={<Typography variant="body2">{statement}</Typography>}
                  sx={{ alignItems: 'flex-start', m: 0 }}
                />
              </Card>
            ))}
          </Stack>

          {/* Procedure Information */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, color: '#555', fontWeight: 'bold' }}>
            Procedure Information
          </Typography>

          <Stack spacing={2}>
            <TextField
              fullWidth label="Type of Piercing" name="typeOfPiercing"
              value={formData.typeOfPiercing} onChange={handleChange} required
            />
            <TextField
              fullWidth label="Type of Jewelry Used" name="typeOfJewelryUsed"
              value={formData.typeOfJewelryUsed} onChange={handleChange} required
            />
            <TextField
              fullWidth label="Name of Piercer" name="nameOfPiercer"
              value={formData.nameOfPiercer} onChange={handleChange} required
            />
          </Stack>

          {/* Signatures */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, color: '#555', fontWeight: 'bold' }}>
            Signatures
          </Typography>

          {/* Guardian Signature */}
          <Box sx={{ mb: 3 }}>
            <SignaturePad ref={guardianSigRef} label="Parent/Guardian Signature" />
            <TextField
              label="Date" type="date" name="date"
              value={formData.date} onChange={handleChange} required
              InputLabelProps={{ shrink: true }}
              sx={{ ml: 2, mt: 1, minWidth: 180 }}
              size="small"
            />
          </Box>

          {/* Piercer Signature */}
          <Box sx={{ mb: 3 }}>
            <SignaturePad ref={piercerSigRef} label="Piercer Signature" />
            <TextField
              label="Date" type="date" name="piercerDate"
              value={formData.piercerDate} onChange={handleChange} required
              InputLabelProps={{ shrink: true }}
              sx={{ ml: 2, mt: 1, minWidth: 180 }}
              size="small"
            />
          </Box>

          {/* Attachments */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, color: '#555', fontWeight: 'bold' }}>
            Required Documents
          </Typography>

          {/* Guardian ID Upload */}
          <Typography variant="h6" sx={{ mb: 1 }}>
            1. Guardian's Photo ID
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please upload a clear photo of the parent/guardian's government-issued ID (driver's license, passport, etc.)
          </Typography>

          {/* Hidden file inputs for guardian ID */}
          <input
            type="file"
            accept="image/*"
            ref={guardianFileInputRef}
            onChange={(e) => handleFileUpload(e, setGuardianIdFile, setGuardianIdPreview, setGuardianIdFileName)}
            style={{ display: 'none' }}
          />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={guardianCameraInputRef}
            onChange={(e) => handleFileUpload(e, setGuardianIdFile, setGuardianIdPreview, setGuardianIdFileName)}
            style={{ display: 'none' }}
          />

          {!guardianIdFile ? (
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => guardianFileInputRef.current?.click()}
              >
                Upload Photo
              </Button>
              <Button
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() => guardianCameraInputRef.current?.click()}
              >
                Take Photo
              </Button>
            </Stack>
          ) : (
            <Box>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                <img
                  src={guardianIdPreview || ''}
                  alt="Guardian ID"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '250px',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                  }}
                />
                <IconButton
                  onClick={removeGuardianId}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                  }}
                  size="small"
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {guardianIdFileName}
              </Typography>
            </Box>
          )}

          {/* Notarized Document Upload */}
          <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
            2. Notarized Consent Document
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please upload a clear photo of the notarized parental/guardian consent document.
          </Typography>

          {/* Hidden file inputs for notarized doc */}
          <input
            type="file"
            accept="image/*"
            ref={notarizedFileInputRef}
            onChange={(e) => handleFileUpload(e, setNotarizedFile, setNotarizedPreview, setNotarizedFileName)}
            style={{ display: 'none' }}
          />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={notarizedCameraInputRef}
            onChange={(e) => handleFileUpload(e, setNotarizedFile, setNotarizedPreview, setNotarizedFileName)}
            style={{ display: 'none' }}
          />

          {!notarizedFile ? (
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => notarizedFileInputRef.current?.click()}
              >
                Upload Photo
              </Button>
              <Button
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() => notarizedCameraInputRef.current?.click()}
              >
                Take Photo
              </Button>
            </Stack>
          ) : (
            <Box>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                <img
                  src={notarizedPreview || ''}
                  alt="Notarized Document"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '250px',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                  }}
                />
                <IconButton
                  onClick={removeNotarizedDoc}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                  }}
                  size="small"
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {notarizedFileName}
              </Typography>
            </Box>
          )}

          {/* Submit */}
          <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 4 }} disabled={submitting}>
            {submitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Uploading & Submitting...
              </Box>
            ) : 'Submit Consent Form'}
          </Button>
        </Box>
      </Card>

      {/* Success Modal */}
      <Dialog
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        aria-labelledby="success-dialog-title"
      >
        <DialogTitle id="success-dialog-title">✅ Form Submitted</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your consent form has been submitted successfully! Thank you.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessModalOpen(false)} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Under18ConsentForm;
